import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "@/lib/api";
import { Sparkles, Users, Gamepad2, BookOpen, ChevronRight, Swords } from "lucide-react";
import { getApiUrl } from "@/lib/api";
import PlatformBadge, { getSystemDisplayName } from "@/components/PlatformBadge";

const API = `${getApiUrl()}/api`;

const COMMUNITY_LABELS = {
  news: "news post",
  challenges: "challenge",
  organizers: "organizer",
  planners: "planner",
  checklists: "checklist",
  grids: "grid",
  maps: "map",
  trivia: "trivia fact",
};
const COMMUNITY_ORDER = ["news", "challenges", "organizers", "planners", "checklists", "grids", "maps", "trivia"];

const plural = (n, label) => `${n} ${label}${n > 1 ? "s" : ""}`;
const namesList = (names, total) => {
  const shown = names.slice(0, 3).join(", ");
  const extra = total - Math.min(names.length, 3);
  return extra > 0 ? `${shown} +${extra} more` : shown;
};
// "God of War (PS5), Elden Ring (PS5) +2 more" — name with its system in parentheses.
const gamesOverview = (games, total) => {
  const shown = games.slice(0, 3).map((g) => {
    const sys = getSystemDisplayName(g.primarySystem || g.systems?.[0] || "");
    return sys ? `${g.name} (${sys})` : g.name;
  }).join(", ");
  const extra = total - Math.min(games.length, 3);
  return extra > 0 ? `${shown} +${extra} more` : shown;
};

export default function WhatsNewStrip() {
  const navigate = useNavigate();
  const [community, setCommunity] = useState({ total: 0, counts: {} });
  const [communityThumbs, setCommunityThumbs] = useState([]);
  const [browseGames, setBrowseGames] = useState([]);
  const [tutTotal, setTutTotal] = useState(0);
  const [tutTitles, setTutTitles] = useState([]);
  const [tutThumbs, setTutThumbs] = useState([]);
  const [mpTotal, setMpTotal] = useState(0);

  const load = useCallback(async () => {
    const [c, bg, ts, mp] = await Promise.all([
      axios.get(`${API}/community/notifications/summary?preview=true`).catch(() => ({ data: { total: 0, counts: {}, thumbnails: [] } })),
      axios.get(`${API}/master/browse/notifications/games`).catch(() => ({ data: { games: [] } })),
      axios.get(`${API}/tutorials/notifications/summary`).catch(() => ({ data: { total: 0, unreadIds: [] } })),
      axios.get(`${API}/multiplayer/notifications/summary`).catch(() => ({ data: { total: 0 } })),
    ]);
    setCommunity(c.data || { total: 0, counts: {} });
    setCommunityThumbs(c.data?.thumbnails || []);
    setBrowseGames(bg.data?.games || []);
    setMpTotal(mp.data?.total || 0);
    const tIds = ts.data?.unreadIds || [];
    setTutTotal(ts.data?.total || 0);
    if (tIds.length) {
      try {
        const list = await axios.get(`${API}/tutorials?sort=newest`).then((r) => r.data || []);
        const idSet = new Set(tIds);
        const unreadTuts = list.filter((t) => idSet.has(t.id));
        setTutTitles(unreadTuts.map((t) => t.title));
        setTutThumbs(unreadTuts.map((t) => t.coverImage).filter(Boolean));
      } catch (e) {
        setTutTitles([]);
        setTutThumbs([]);
      }
    } else {
      setTutTitles([]);
      setTutThumbs([]);
    }
  }, []);

  useEffect(() => {
    load();
    const refresh = () => load();
    window.addEventListener("unread-refresh", refresh);
    window.addEventListener("community-unread-refresh", refresh);
    return () => {
      window.removeEventListener("unread-refresh", refresh);
      window.removeEventListener("community-unread-refresh", refresh);
    };
  }, [load]);

  const communityTotal = community?.total || 0;
  const browseTotal = browseGames.length;
  const grandTotal = communityTotal + browseTotal + tutTotal + mpTotal;

  if (grandTotal === 0) return null;

  const communityOverview = COMMUNITY_ORDER
    .filter((t) => (community?.counts?.[t] || 0) > 0)
    .map((t) => plural(community.counts[t], COMMUNITY_LABELS[t]))
    .join(" · ");

  const rows = [];
  if (communityTotal > 0) {
    rows.push({
      key: "community",
      icon: Users,
      accent: "text-purple-400",
      chip: "bg-purple-500/20 text-purple-300",
      title: "Community Items",
      count: communityTotal,
      overview: communityOverview || "New community content",
      to: "/social",
      thumbs: communityThumbs,
    });
  }
  if (browseTotal > 0) {
    rows.push({
      key: "browse",
      icon: Gamepad2,
      accent: "text-emerald-400",
      chip: "bg-emerald-500/20 text-emerald-300",
      title: "New Games",
      count: browseTotal,
      overview: gamesOverview(browseGames, browseTotal),
      to: "/browse",
      thumbs: browseGames
        .filter((g) => g.boxArtUrl)
        .map((g) => ({ src: g.boxArtUrl, platform: g.platforms?.[0] || null })),
    });
  }
  if (tutTotal > 0) {
    rows.push({
      key: "tutorials",
      icon: BookOpen,
      accent: "text-amber-400",
      chip: "bg-amber-500/20 text-amber-300",
      title: "Tutorials & FAQs",
      count: tutTotal,
      overview: tutTitles.length ? namesList(tutTitles, tutTotal) : `${tutTotal} tutorial${tutTotal > 1 ? "s" : ""} to explore`,
      to: "/tutorials",
      thumbs: tutThumbs,
    });
  }
  if (mpTotal > 0) {
    rows.push({
      key: "multiplayer",
      icon: Swords,
      accent: "text-cyan-400",
      chip: "bg-cyan-500/20 text-cyan-300",
      title: "Multiplayer Matches",
      count: mpTotal,
      overview: `${mpTotal} player${mpTotal > 1 ? "s" : ""} on your games with a matching schedule`,
      to: "/multiplayer",
      thumbs: [],
    });
  }

  return (
    <div
      data-testid="whats-new-strip"
      className="rounded-xl border border-white/10 bg-gradient-to-r from-[#141018] via-[#0F0F14] to-[#0A0A0A] overflow-hidden"
    >
      <div className="flex items-center gap-2 px-5 pt-4 pb-2">
        <div className="p-1.5 rounded-lg bg-red-500/15">
          <Sparkles className="w-4 h-4 text-red-400" />
        </div>
        <h2 className="text-white font-heading font-semibold text-lg">What's New</h2>
        <span className="ml-1 min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white" data-testid="whats-new-total">
          {grandTotal > 99 ? "99+" : grandTotal}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
        {rows.map((r) => (
          <button
            key={r.key}
            onClick={() => navigate(r.to)}
            className="group flex items-center gap-3 text-left px-5 py-4 bg-[#0A0A0A] hover:bg-white/[0.04] transition-colors"
            data-testid={`whats-new-${r.key}`}
          >
            {r.thumbs && r.thumbs.length > 0 ? (
              <div className="flex shrink-0 -space-x-3" data-testid={`whats-new-thumbs-${r.key}`}>
                {r.thumbs.slice(0, 3).map((t, i) => {
                  const src = typeof t === "string" ? t : t.src;
                  const platform = typeof t === "object" ? t.platform : null;
                  return (
                    <div key={i} className="relative w-8 h-11 shrink-0" style={{ zIndex: 3 - i }}>
                      <img
                        src={src}
                        alt=""
                        loading="lazy"
                        className="w-8 h-11 rounded-md object-cover border-2 border-[#0A0A0A] bg-black/40 shadow-sm"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                      {platform && (
                        <span className="absolute -bottom-1 -right-1" data-testid={`whats-new-thumb-platform-${r.key}-${i}`}>
                          <PlatformBadge platform={platform} size="xs" showLabel={false} />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={`p-2 rounded-lg bg-white/5 ${r.accent} shrink-0`}>
                <r.icon className="w-5 h-5" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium text-sm">{r.title}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${r.chip}`}>{r.count} new</span>
              </div>
              <p className="text-white/50 text-xs mt-0.5 truncate" title={r.overview}>{r.overview}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-white/60 group-hover:translate-x-0.5 transition-all shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}
